// ============================================
// Time display helpers
// ============================================
//
// One instant, three readings a reviewer needs: the wall clock they were
// standing in (local), the unambiguous one (UTC), and how far into the session
// it happened (T+). The list shows local because that's what a stopwatch on
// the day says; the tooltip carries the rest so nothing is guessed at.

import { t } from "i18n";

const pad = (n, w = 2) => String(n).padStart(w, "0");

/**
 * The viewing machine's UTC offset at a given instant, e.g. "UTC+1" — DST
 * aware, so a summer capture reads +1 and a winter one +0. Derived from the
 * date rather than "now": a file opened in January must still label an August
 * session correctly.
 */
export function tzOffsetLabel(ms) {
  const d = ms == null ? new Date() : new Date(ms);
  if (Number.isNaN(d.getTime())) return "UTC";
  // getTimezoneOffset is minutes BEHIND UTC (west positive), so negate.
  const mins = -d.getTimezoneOffset();
  if (mins === 0) return "UTC";
  const sign = mins > 0 ? "+" : "−";
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${pad(m)}` : ""}`;
}

/** Local wall clock, seconds precision: "15:41:42". */
export function fmtLocalTime(ms) {
  return new Date(ms).toLocaleTimeString(undefined, { hour12: false });
}

/** UTC wall clock, seconds precision: "14:41:42". */
export function fmtUtcTime(ms) {
  const d = new Date(ms);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(
    d.getUTCSeconds()
  )}`;
}

/**
 * Elapsed time since the session began, as H:MM:SS ("3:49:28"). Negative
 * input (a record stamped before the start marker — clock nudged mid-session)
 * keeps its sign rather than wrapping into a nonsense positive.
 *
 * `compact` drops an empty hours field ("26:12" rather than "0:26:12") — in a
 * dense table the leading zero is three characters of noise on every row.
 */
export function fmtElapsed(deltaMs, { compact = false } = {}) {
  if (deltaMs == null || Number.isNaN(deltaMs)) return null;
  const neg = deltaMs < 0;
  const total = Math.floor(Math.abs(deltaMs) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const sign = neg ? "−" : "";
  if (compact && h === 0) return `${sign}${m}:${pad(s)}`;
  return `${sign}${h}:${pad(m)}:${pad(s)}`;
}

/**
 * Multi-line hover text for a record's instant: every reading at once, so the
 * column's local time is never mistaken for UTC (or vice versa).
 *
 * @param {number} ms            record timestamp
 * @param {boolean} isEpoch      false ⇒ device uptime, not a wall clock
 * @param {number|null} startMs  session start, for the T+ line
 */
export function timeTooltip(ms, isEpoch, startMs = null) {
  if (ms == null) return "";
  if (!isEpoch) {
    return [
      t("utils.timeDisplay.deviceUptime", {
        seconds: (ms / 1000).toFixed(1),
      }),
      t("utils.timeDisplay.clockNeverSynced"),
    ].join("\n");
  }
  const d = new Date(ms);
  const lines = [
    t("utils.timeDisplay.localLine", {
      time: d.toLocaleString(undefined, { hour12: false }),
      offset: tzOffsetLabel(ms),
    }),
    t("utils.timeDisplay.utcLine", {
      time: d.toISOString().replace("T", " ").slice(0, 19),
    }),
  ];
  const elapsed = startMs != null ? fmtElapsed(ms - startMs) : null;
  if (elapsed) lines.push(t("utils.timeDisplay.elapsedLine", { elapsed }));
  return lines.join("\n");
}
